import { deriveJobDurationMonths } from "../../../utils/incomeEmploymentValidator";
import type { IncomeEmploymentFormData } from "../../../utils/incomeEmploymentValidator";
import type { EmploymentFormRow } from "../../../types/tabs";
import {
  isUnitedStatesCountry,
  mapOtherIncomeFormsToApi,
  parseOtherIncomePresentJob,
} from "./incomeEmploymentHelpers";
import type {
  EmploymentJobApiRow,
  OtherIncomeApiRow,
  OtherIncomeFormRow,
} from "../../../types/incomeEmploymentApi";

/** Parse API / user date values into HTML date input format (YYYY-MM-DD). */
export function parseIncomeEmploymentDate(dateValue: unknown): string {
  if (!dateValue) return "";
  if (typeof dateValue === "string") {
    if (dateValue.includes("T")) {
      return dateValue.split("T")[0];
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      const [day, month, year] = dateValue.split("/");
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  }
  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
}

export function extractEmploymentJobsFromApplication(applicationData: {
  current_job?: unknown;
  current_employment?: unknown;
  employmentinfo?: {
    current_employment?: unknown;
    previous_employment?: unknown;
  };
  employment_info?: {
    current_employment?: unknown;
    previous_employment?: unknown;
  };
  previous_job?: unknown;
  previous_employment?: unknown;
  resident_other_source?: unknown;
  other_income?: unknown;
}): {
  currentJobs: EmploymentJobApiRow[];
  previousJobs: EmploymentJobApiRow[];
  otherIncomeData: OtherIncomeApiRow[];
} {
  const currentJobsRaw =
    applicationData?.current_job ??
    applicationData?.current_employment ??
    applicationData?.employmentinfo?.current_employment ??
    applicationData?.employment_info?.current_employment;
  const currentJobs: EmploymentJobApiRow[] = Array.isArray(currentJobsRaw)
    ? (currentJobsRaw as EmploymentJobApiRow[])
    : currentJobsRaw
      ? [currentJobsRaw as EmploymentJobApiRow]
      : [];

  const previousJobsRaw =
    applicationData?.previous_job ??
    applicationData?.previous_employment ??
    applicationData?.employmentinfo?.previous_employment ??
    applicationData?.employment_info?.previous_employment;
  const previousJobs: EmploymentJobApiRow[] = Array.isArray(previousJobsRaw)
    ? (previousJobsRaw as EmploymentJobApiRow[])
    : previousJobsRaw
      ? [previousJobsRaw as EmploymentJobApiRow]
      : [];

  const otherIncomeDataRaw =
    applicationData?.resident_other_source ||
    applicationData?.other_income ||
    [];
  const otherIncomeData: OtherIncomeApiRow[] = Array.isArray(otherIncomeDataRaw)
    ? (otherIncomeDataRaw as OtherIncomeApiRow[])
    : otherIncomeDataRaw
      ? [otherIncomeDataRaw as OtherIncomeApiRow]
      : [];

  return { currentJobs, previousJobs, otherIncomeData };
}

export function buildEmptyEmploymentRow(): EmploymentFormRow {
  return {
    job_id: "",
    company_name: "",
    employment_type: "FULL_TIME",
    start_date: "",
    end_date: "",
    monthly_income: "",
    income_period: "Per_Month",
    job_duration: "",
    employer_street_address: "",
    company_address: "",
    company_city: "",
    company_state: "",
    company_zipcode: "",
    company_country: "USA",
    hr_manager_contact_email: "",
    employer_website: "",
    distance_explain: "",
    job_distance: "",
    job_gap: "",
    job_gap_explanation: "",
  };
}

export function buildEmploymentRowFromJob(
  job: EmploymentJobApiRow,
): EmploymentFormRow {
  return {
    job_id: job?.job_id || "",
    company_name: job?.company_name || job?.employer_name || "",
    employment_type: job?.employment_type || job?.job_type || "FULL_TIME",
    start_date: parseIncomeEmploymentDate(job?.start_date || job?.job_start_date),
    end_date: parseIncomeEmploymentDate(job?.end_date || job?.job_end_date),
    monthly_income: job?.monthly_income || job?.income || "",
    income_period: job?.income_period || "Per_Month",
    job_duration: job?.job_duration || "",
    employer_street_address:
      job?.employer_street_address ||
      [
        job?.company_address || job?.employer_street,
        job?.company_city || job?.employer_city,
        job?.company_state || job?.employer_state,
        job?.company_zipcode || job?.employer_zip_code,
      ]
        .filter((p) => p && String(p).trim())
        .join(", "),
    company_address: job?.company_address || job?.employer_street || "",
    company_city: job?.company_city || job?.employer_city || "",
    company_state: job?.company_state || job?.employer_state || "",
    company_zipcode: job?.company_zipcode || job?.employer_zip_code || "",
    company_country: (() => {
      const raw = job?.company_country || job?.employer_country || "USA";
      return isUnitedStatesCountry(raw) ? "USA" : String(raw || "");
    })(),
    hr_manager_contact_email:
      job?.hr_manager_contact_email || job?.supervisor_email || "",
    employer_website: job?.employer_website || "",
    distance_explain: job?.distance_explain || "",
    job_distance: job?.job_distance ?? job?.distance ?? "",
    job_gap: job?.job_gap ?? "",
    job_gap_explanation: job?.job_gap_explanation || "",
  };
}

export function buildOtherIncomeFormsFromApi(
  rows: OtherIncomeApiRow[],
): OtherIncomeFormRow[] {
  return rows.map((income) => {
    const presentJob = parseOtherIncomePresentJob(income);
    return {
      job_id: income.job_id || "",
      income_period: income.income_period || income.period || "Per_Month",
      source_name:
        income.source_name || income.income_source || income.type || "",
      income: income.income || income.amount || "",
      job_start_date: parseIncomeEmploymentDate(
        income.job_start_date || income.start_date,
      ),
      job_end_date: presentJob
        ? ""
        : parseIncomeEmploymentDate(income.job_end_date || income.end_date),
      present_job: presentJob,
    };
  });
}

export function isEmploymentRowMeaningful(row: EmploymentFormRow): boolean {
  return Boolean(
    String(row.company_name ?? "").trim() ||
      String(row.employer_street_address ?? "").trim() ||
      String(row.company_address ?? "").trim() ||
      String(row.company_city ?? "").trim() ||
      String(row.monthly_income ?? "").trim() ||
      String(row.start_date ?? "").trim(),
  );
}

export function mapEmploymentFormToApi(
  form: EmploymentFormRow,
  isPrevious: boolean,
): Record<string, unknown> {
  const baseData: Record<string, unknown> = {
    job_id: form.job_id || "",
    job_type: form.employment_type || "FULL_TIME",
    employer_name: form.company_name || "",
    employer_street_address: form.employer_street_address || "",
    outside_us: !isUnitedStatesCountry(form.company_country),
    employer_street: form.company_address || "",
    employer_city: form.company_city || "",
    employer_state: form.company_state || "",
    employer_zip_code: form.company_zipcode || "",
    employer_country: isUnitedStatesCountry(form.company_country)
      ? "USA"
      : form.company_country || "USA",
    income: form.monthly_income || "",
    income_period: form.income_period || "Per_Month",
    job_duration:
      deriveJobDurationMonths(form.start_date, form.end_date, {
        treatAsPresentJob: !isPrevious,
      }) ||
      String(form.job_duration ?? "").trim() ||
      "",
    job_start_date: form.start_date || "",
    employer_website: form.employer_website || "",
    hr_manager_contact_email: form.hr_manager_contact_email || "",
  };

  if (isPrevious) {
    baseData.job_end_date = form.end_date || "";
    baseData.job_gap = form.job_gap || "";
    baseData.job_gap_explanation = form.job_gap_explanation || "";
  } else {
    baseData.present_job = "1";
    baseData.job_end_date = null;
    baseData.distance_explain = form.distance_explain || "";
    baseData.job_distance = form.job_distance || "";
  }

  return baseData;
}

export function buildEmploymentApiPayload(
  options: {
    currentRows: EmploymentFormRow[];
    previousRows: EmploymentFormRow[];
    otherRows: Record<string, unknown>[];
    employmentStatus: string;
    applicationData?: {
      underwriter_verified_income_other?: unknown;
      underwriter_verified_income_type?: unknown;
    };
    employmentStatusOverride?: string;
  },
): Record<string, unknown> {
  const currentArray = options.currentRows
    .filter(isEmploymentRowMeaningful)
    .map((row) => mapEmploymentFormToApi(row, false));
  const previousArray = options.previousRows
    .filter(isEmploymentRowMeaningful)
    .map((row) => mapEmploymentFormToApi(row, true));

  const apiData: Record<string, unknown> = {
    employment_status:
      options.employmentStatusOverride ?? options.employmentStatus ?? "",
    previous_employer: previousArray.length > 0 ? "1" : "0",
    current_employment: currentArray,
    previous_employment: previousArray,
    other_source: mapOtherIncomeFormsToApi(options.otherRows),
  };

  if (options.applicationData?.underwriter_verified_income_other !== undefined) {
    apiData.underwriter_verified_income_other =
      options.applicationData.underwriter_verified_income_other;
  }
  if (options.applicationData?.underwriter_verified_income_type !== undefined) {
    apiData.underwriter_verified_income_type =
      options.applicationData.underwriter_verified_income_type;
  }

  return apiData;
}

export function patchEmploymentRow(
  row: EmploymentFormRow,
  field: string,
  value: unknown,
  isPrevious: boolean,
): EmploymentFormRow {
  const updated = { ...row, [field]: value } as EmploymentFormRow;
  if (field === "start_date" || field === "end_date") {
    updated.job_duration = deriveJobDurationMonths(
      updated.start_date,
      updated.end_date,
      { treatAsPresentJob: !isPrevious },
    );
  }
  return updated;
}

export function patchEmploymentRowFields(
  row: EmploymentFormRow,
  updates: Partial<EmploymentFormRow>,
  isPrevious: boolean,
): EmploymentFormRow {
  const updated = { ...row, ...updates } as EmploymentFormRow;
  if (
    updates.start_date !== undefined ||
    updates.end_date !== undefined
  ) {
    updated.job_duration = deriveJobDurationMonths(
      updated.start_date,
      updated.end_date,
      { treatAsPresentJob: !isPrevious },
    );
  }
  return updated;
}

/** Angular `previous_employer` — when explicitly "0", previous rows are not active. */
export function isPreviousEmployerActiveOnApplication(
  applicationData: Record<string, unknown>,
  previousJobCount: number,
): boolean {
  const flag = applicationData?.previous_employer;
  if (flag === "0" || flag === 0 || flag === false) return false;
  if (flag === "1" || flag === 1 || flag === true) return true;
  return previousJobCount > 0;
}

function mapExtractedJobsToApiInput(
  jobs: EmploymentJobApiRow[],
  isPrevious: boolean,
): NonNullable<IncomeEmploymentFormData["current_employment"]> {
  return jobs
    .map(buildEmploymentRowFromJob)
    .filter(isEmploymentRowMeaningful)
    .map((row) => mapEmploymentFormToApi(row, isPrevious));
}

/** Income/employment snapshot for save and tab-switch validation (Angular edit form shape). */
export function buildIncomeEmploymentEffectiveData(
  formData: Record<string, unknown>,
  applicationData: Record<string, unknown>,
): IncomeEmploymentFormData {
  const { currentJobs, previousJobs, otherIncomeData } =
    extractEmploymentJobsFromApplication(applicationData);

  const employmentStatus =
    (formData.employment_status as IncomeEmploymentFormData["employment_status"]) ??
    (applicationData.employment_status as IncomeEmploymentFormData["employment_status"]);

  const hasFormCurrent = Object.prototype.hasOwnProperty.call(
    formData,
    "current_employment",
  );
  const hasFormPrevious = Object.prototype.hasOwnProperty.call(
    formData,
    "previous_employment",
  );
  const hasFormOther = Object.prototype.hasOwnProperty.call(formData, "other_source");

  const includePrevious =
    hasFormPrevious ||
    isPreviousEmployerActiveOnApplication(applicationData, previousJobs.length);

  const current_employment = hasFormCurrent
    ? ((formData.current_employment as
        | IncomeEmploymentFormData["current_employment"]
        | undefined) ?? [])
    : employmentStatus === "1"
      ? mapExtractedJobsToApiInput(currentJobs, false)
      : [];

  const previous_employment = hasFormPrevious
    ? ((formData.previous_employment as
        | IncomeEmploymentFormData["previous_employment"]
        | undefined) ?? [])
    : includePrevious
      ? mapExtractedJobsToApiInput(previousJobs, true)
      : [];

  const other_source = hasFormOther
    ? ((formData.other_source as IncomeEmploymentFormData["other_source"]) ?? [])
    : (mapOtherIncomeFormsToApi(
        buildOtherIncomeFormsFromApi(otherIncomeData) as Record<string, unknown>[],
      ) as unknown as IncomeEmploymentFormData["other_source"]);

  return {
    employment_status: employmentStatus,
    current_employment,
    previous_employment,
    other_source,
  };
}
